import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, PackagePlus } from 'lucide-react';
import { useProducts } from '@/hooks/useProducts';
import { useCreateProductBundle } from '@/hooks/useProductBundles';

interface BundleItem {
  product_id: string;
  quantity: number;
  product_name?: string;
  product_price?: number;
}

interface QuickBundleCreateDialogProps {
  onBundleCreated?: (bundleId: string) => void;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function QuickBundleCreateDialog({ onBundleCreated }: QuickBundleCreateDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [bundlePrice, setBundlePrice] = useState('');
  const [discountPercent, setDiscountPercent] = useState('10');
  const [items, setItems] = useState<BundleItem[]>([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const { data: products } = useProducts();
  const createBundle = useCreateProductBundle();

  const activeProducts = products?.filter(p => p.is_active) || [];

  const addItem = () => {
    if (!selectedProductId) return;
    const product = products?.find(p => p.id === selectedProductId);
    if (!product) return;

    // Check if already added
    if (items.some(i => i.product_id === selectedProductId)) {
      // Increment quantity
      setItems(items.map(i => 
        i.product_id === selectedProductId 
          ? { ...i, quantity: i.quantity + 1 }
          : i
      ));
    } else {
      setItems([...items, {
        product_id: selectedProductId,
        quantity: 1,
        product_name: product.name,
        product_price: product.base_price,
      }]);
    }
    setSelectedProductId('');
  };

  const removeItem = (productId: string) => {
    setItems(items.filter(i => i.product_id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity < 1) return;
    setItems(items.map(i => 
      i.product_id === productId ? { ...i, quantity } : i
    ));
  };

  const totalOriginalPrice = items.reduce((sum, item) => 
    sum + (item.product_price || 0) * item.quantity, 0
  );

  const calculatedBundlePrice = bundlePrice 
    ? parseFloat(bundlePrice) 
    : totalOriginalPrice * (1 - parseFloat(discountPercent || '0') / 100);

  const handleCreate = async () => {
    if (!name || items.length === 0) return;

    const result = await createBundle.mutateAsync({
      name,
      description: description || undefined,
      bundle_price: calculatedBundlePrice,
      currency: 'USD',
      discount_type: 'percentage',
      discount_value: parseFloat(discountPercent || '0'),
      is_active: true,
      items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
    });

    if (result?.id && onBundleCreated) {
      onBundleCreated(result.id);
    }

    // Reset form
    setName('');
    setDescription('');
    setBundlePrice('');
    setDiscountPercent('10');
    setItems([]);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <PackagePlus className="h-4 w-4 mr-2" />
          Create Bundle
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Quick Bundle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Bundle Name</Label>
            <Input 
              value={name} 
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Starter Package"
            />
          </div>

          <div>
            <Label>Description (Optional)</Label>
            <Textarea 
              value={description} 
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the bundle"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Add Products</Label>
            <div className="flex gap-2">
              <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {activeProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} - {formatCurrency(product.base_price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={addItem} disabled={!selectedProductId}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {items.length > 0 && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
              <Label className="text-sm font-medium">Bundle Items</Label>
              {items.map((item) => (
                <div key={item.product_id} className="flex items-center justify-between gap-2">
                  <span className="text-sm flex-1 truncate">{item.product_name}</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value) || 1)}
                      className="w-16 h-8 text-center"
                    />
                    <span className="text-sm text-muted-foreground w-20 text-right">
                      {formatCurrency((item.product_price || 0) * item.quantity)}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive"
                      onClick={() => removeItem(item.product_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t flex justify-between items-center">
                <span className="text-sm">Original Total</span>
                <span className="font-medium">{formatCurrency(totalOriginalPrice)}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Discount %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={discountPercent}
                onChange={(e) => {
                  setDiscountPercent(e.target.value);
                  setBundlePrice(''); // Clear custom price when changing discount
                }}
              />
            </div>
            <div>
              <Label>Bundle Price</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={bundlePrice || calculatedBundlePrice.toFixed(2)}
                onChange={(e) => setBundlePrice(e.target.value)}
                placeholder="Auto-calculated"
              />
            </div>
          </div>

          {items.length > 0 && (
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
              <span className="text-sm font-medium">Final Bundle Price</span>
              <div className="flex items-center gap-2">
                {totalOriginalPrice > calculatedBundlePrice && (
                  <span className="text-sm line-through text-muted-foreground">
                    {formatCurrency(totalOriginalPrice)}
                  </span>
                )}
                <Badge variant="default" className="text-lg px-3 py-1">
                  {formatCurrency(calculatedBundlePrice)}
                </Badge>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCreate} 
            disabled={!name || items.length === 0 || createBundle.isPending}
          >
            {createBundle.isPending ? 'Creating...' : 'Create Bundle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
